import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { getActiveCompanyId } from "../../lib/companyContext";

const parseEmployeesPayload = (raw) => {
  if (!raw) return { items: [], totalCount: 0 };

  const hydrate = (value) => {
    if (!value) return null;
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  };

  const normalized = hydrate(raw.result) ?? hydrate(raw?.data) ?? hydrate(raw) ?? {};

  if (Array.isArray(normalized)) {
    return { items: normalized, totalCount: normalized.length };
  }

  if (Array.isArray(normalized.items)) {
    return {
      items: normalized.items,
      totalCount: normalized.totalCount ?? normalized.items.length,
    };
  }

  if (Array.isArray(normalized.data)) {
    return {
      items: normalized.data,
      totalCount: normalized.totalCount ?? normalized.data.length,
    };
  }

  return { items: [], totalCount: 0 };
};

export function useGetEverifyEmployees({
  page = 1,
  pageSize = 20,
  search = "",
  statuses = [],
  sortColumn = "Date",
  sortOrder = "Desc",
} = {}) {
  const allowedSortColumns = new Set([
    "Date",
    "FirstName",
    "LastName",
    "Status",
    "InitiatorFirstName",
    "InitiatorLastName",
  ]);
  const resolvedSortColumn = allowedSortColumns.has(sortColumn)
    ? sortColumn
    : "Date";

  return useQuery({
    queryKey: [
      "everifyEmployees",
      getActiveCompanyId(),
      page,
      pageSize,
      search,
      statuses.join(","),
      resolvedSortColumn,
      sortOrder,
    ],
    queryFn: async () => {
      const payload = {
        companyId: getActiveCompanyId(),
        pageNumber: page,
        pageSize,
        status: Array.isArray(statuses) ? statuses : [],
        search: search?.trim() ?? "",
        sortColumn: resolvedSortColumn,
        sortOrder,
        exportCsv: false,
      };

      const { data } = await api.post("/everify/geteverifyemployees", payload);
      const parsed = parseEmployeesPayload(data);

      return {
        employees: parsed.items,
        total: parsed.totalCount,
        pageNumber: page,
        pageSize,
      };
    },
    keepPreviousData: true,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
  });
}
